import { useState } from 'react';
import { httpClient } from '../api/http';
import { Alert } from '@piggytechng/component-library';
import { ApiResponse, HmNuban, HouseMoneyTransaction, QuickActionPayloadProps } from '../types';
import { authPayload } from '../helpers/utils/auth-payload';
import { useParams } from 'react-router-dom';
import { setHouseMoney, setModalActions } from '../store/slices/utils';
import { useDispatch } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import useRolePermissions from './useRolePermissions';

// import { useAppDispatch } from '../store';

const GATED_QUICK_ACTIONS: Record<string, string> = {
  edit_hm_withdraw_month: 'changeEndDate',
  fix_interest: 'fixInterest',
};

const useHouseMoney = () => {
  const params = useParams();
  const userIdIn = params.id as string;
  const dispatch = useDispatch();
  const { canPerformAction } = useRolePermissions({});

  const isGatedActionBlocked = (action?: string) => {
    const permission = action ? GATED_QUICK_ACTIONS[action] : undefined;
    if (permission && !canPerformAction('housemoney', permission)) {
      Alert.warn('You do not have access to perform this action.');
      return true;
    }
    return false;
  };

  const [setHouseMoneyDetails] = useState<any>({});
  const [houseMoneyTransactions, setHouseMoneyTransactions] = useState<HouseMoneyTransaction[]>([]);

  const [isGeneratingAccount, setIsGeneratingAccount] = useState<boolean>(false);
  const [nubans, setNubans] = useState<HmNuban[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [generatedAccountStatus, setGeneratedAccountStatus] = useState<boolean | null>(null);
  const [transactionsQueryType, setTransactionsQueryType] = useState<'fetch-all' | 'query'>();
  const [authCheckPrompt, setAuthCheckPrompt] = useState<boolean>(false);
  const [quickActionPayload, setQuickActionPayload] = useState<QuickActionPayloadProps>();
  const [quickActionEvent, setQuickActionEvent] = useState<any>();
  const [actionPassword, setActionPassword] = useState('');

  const [searchForm, setSearchForm] = useState({
    query: '',
  });

  const [isSearchingHouseMoney, setIsSearchingHouseMoney] = useState<boolean>(false);
  const [searchedHouseMoneyTransactions, setSearchedHouseMoneyTransactions] = useState<HouseMoneyTransaction[] | null>(
    null,
  );

  const [authCheckForm, setAuthCheckForm] = useState({
    identifier: '',
    password: '',
  });

  const [paginationData, setPaginationData] = useState({
    currentPage: 1,
    nextPage: 2,
    lastPage: 0,
    perPage: 10,
    total: 0,
  });

  const {
    data: houseMoneyDetails,
    isLoading: isLoadingHouseMoneyDetails,
    refetch: refetchHouseMoneyDetails,
  } = useQuery({
    queryKey: ['housemoney-details' + userIdIn],
    queryFn: async () => {
      const res = await httpClient.get(`admin/housemoney/details/${userIdIn}`, { hideError: true });
      if (res?.data?.status && res?.data?.data?.nubans) {
        return res.data.data;
      }
      return null;
    },
  });

  const {
    data: houseMoneyTransactionsQ,
    // isLoading: isLoadingHouseMoneyTransactions,
    // refetch: refetchHouseMoneyTransactions,
  } = useQuery<HouseMoneyTransaction[]>({
    queryKey: ['housemoney-transactions' + userIdIn],
    queryFn: () => httpClient.get(`admin/housemoney/transactions/${userIdIn}/1/10`).then((res) => res.data.data.list),
  });

  const [rentDate, setRentDate] = useState<number>(houseMoneyDetails?.withdrawalMonth);

  const fetchHouseMoneyDetails = async (userId?: string) => {
    console.log(userId);
    try {
      setIsLoading(true);
      const url = `admin/housemoney/details/${userIdIn}`;
      const response = await httpClient.get(url, { hideError: true });
      if (response?.data?.status && response?.data?.data.nubans) {
        setHouseMoneyDetails(response?.data?.data);
        setNubans(response?.data?.data?.nubans || []);
        setTransactionsQueryType('fetch-all');
        dispatch(setHouseMoney(response?.data?.data));
      }
    } catch (error) {
      console.log(error);
      return error;
    } finally {
      setIsLoading(false);
    }
  };

  const fetchHouseMoneyTransactions = async (userId: string, page: number, limit: number) => {
    try {
      setIsLoading(true);
      const url = `admin/housemoney/transactions/${userId}/${page}/${limit}`;

      const response = await httpClient.get(url);
      if (response.data.status) {
        const { list, currentPage, nextPage, lastPage, total } = response.data.data;
        const transactionsPaginationData = {
          currentPage: currentPage + 1,
          nextPage: nextPage,
          lastPage: lastPage,
          total: total,
        };
        setPaginationData({ ...paginationData, ...transactionsPaginationData });
        setHouseMoneyTransactions(list);
        setTransactionsQueryType('fetch-all');
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const searchHouseMoneyTransactions = async (userId: string, query: string, page: number, limit: string) => {
    if (query === '') {
      Alert.error('Search cannot be empty');
      return;
    }
    try {
      setIsLoading(true);
      const url = `admin/housemoney/search/transactions?userId=${userId}&query=${query}&page=${page}&limit=${limit}`;
      const response = await httpClient.get(url);
      if (response?.data?.status) {
        const { list, currentPage, nextPage, lastPage, total } = response.data.data;
        const transactionsPaginationData = {
          currentPage: currentPage + 1,
          nextPage: nextPage,
          lastPage: lastPage,
          total: total,
        };
        setPaginationData({ ...paginationData, ...transactionsPaginationData });
        setHouseMoneyTransactions(list);
        setTransactionsQueryType('query');
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const searchHouseMoney = async (query: string, page = 1, limit = 10) => {
    if (!query.trim()) {
      Alert.error('Please enter a search value');
      return;
    }
    try {
      setIsSearchingHouseMoney(true);
      const url = `admin/housemoney/search/transactions?userId=${userIdIn}&query=${query.trim()}&page=${page}&limit=${limit}`;
      const response = await httpClient.get(url);
      if (response?.data?.status) {
        setSearchedHouseMoneyTransactions(response.data.data?.list ?? []);
      }
    } catch (error: any) {
      Alert.error(error?.response?.data?.message || 'Failed to search transactions');
    } finally {
      setIsSearchingHouseMoney(false);
    }
  };

  const clearHouseMoneySearch = () => {
    setSearchedHouseMoneyTransactions(null);
  };

  const handleChange = (e: { target: { name: string; value: string } }) => {
    const { value } = e.target;
    setSearchForm({ query: value });
  };

  const handleQuickAction = async (userId: string, action: string, options?: any) => {
    if (isGatedActionBlocked(action)) return;

    let payload: QuickActionPayloadProps;

    if (action === 'edit_hm_withdraw_month') {
      payload = {
        userId,
        action,
        rentMonth: options.rentDate + 1,
      };
    } else if (action === 'fix_interest') {
      const formattedDate = `${options.fixInterestMonth}-${options.fixInterestYear}`;
      payload = {
        userId,
        action,
        period: formattedDate,
      };
    } else if (action === 'generate_hm_nuban') {
      payload = {
        userId,
        action,
      };
    } else if (action === 'delete_hm_nuban') {
      payload = {
        userId,
        action,
        accountNumber: options,
      };
    } else if (action === 'fix_account_balance') {
      payload = {
        userId,
        action,
      };
    } else {
      payload = { userId, action };
    }

    setQuickActionPayload(payload);
    setQuickActionEvent(action);

    setAuthCheckPrompt(true);
  };

  const handleHouseMoneyQuickAction = async (fn?: () => void, action?: string, options?: any, password?: string) => {
    if (isGatedActionBlocked(action)) return;

    setIsLoading(true);

    let payload: QuickActionPayloadProps;

    if (action === 'edit_hm_withdraw_month') {
      payload = {
        userId: userIdIn,
        action,
        rentMonth: options.rentDate + 1,
      };
    } else if (action === 'fix_interest') {
      const formattedDate = `${options.fixInterestMonth}-${options.fixInterestYear}`;
      payload = {
        userId: userIdIn,
        action,
        period: formattedDate,
      };
    } else if (action === 'generate_hm_nuban') {
      payload = {
        userId: userIdIn,
        action,
      };
    } else if (action === 'fix_account_balance') {
      payload = {
        userId: userIdIn,
        action,
      };
    } else {
      payload = {
        userId: userIdIn,
        action: 'delete_hm_nuban',
        accountNumber: quickActionPayload?.accountNumber,
      };
    }

    Object.assign(payload, authPayload(password ?? ''));

    try {
      const url = `admin/housemoney/quick-actions`;
      const response: ApiResponse = await httpClient.post(url, payload);
      if (response?.data.status) {
        Alert.success(response?.data.message || 'Action executed successfully');
        if (fn) fn();
        dispatch(setModalActions({}));
        refetchHouseMoneyDetails();
      }
      if (action === 'generate_hm_nuban') {
        setGeneratedAccountStatus(true);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    fetchHouseMoneyDetails,
    houseMoneyDetails,
    fetchHouseMoneyTransactions,
    searchHouseMoneyTransactions,
    searchHouseMoney,
    clearHouseMoneySearch,
    searchedHouseMoneyTransactions,
    isSearchingHouseMoney,
    houseMoneyTransactions,
    rentDate,
    setRentDate,
    handleQuickAction,
    quickActionPayload,
    quickActionEvent,
    generatedAccountStatus,
    setGeneratedAccountStatus,
    paginationData,
    transactionsQueryType,
    handleChange,
    searchForm,
    isLoading,
    setIsLoading,
    isGeneratingAccount,
    setIsGeneratingAccount,
    nubans,
    setNubans,
    authCheckPrompt,
    setAuthCheckPrompt,
    authCheckForm,
    setAuthCheckForm,
    setQuickActionPayload,
    setActionPassword,
    actionPassword,
    handleHouseMoneyQuickAction,
    isLoadingHouseMoneyDetails,
    houseMoneyTransactionsQ,
  };
};

export default useHouseMoney;
